<template>
  <div class="aspect-ratio-wrapper">
    <iframe
      ref="iframe"
      class="game-engine"
      src="/sketch.html"
      @load="onIframeLoad"
    ></iframe>
  </div>
</template>

<script>
import {
  getStorage,
  ref as storageRef,
  listAll,
  getDownloadURL,
  getMetadata
} from 'firebase/storage';
import { firebaseApp } from '@/firebase/init';

export default {

  name: 'GameEngine',
  
  props: {
    codeString: { type: String,  required: true  },
    game: { type: Object,  required: true  }
  },

  data() {
    return {
      soundsMap: {},
      imagesMap: {}
    };
  },
  mounted() {
    window.addEventListener('message', this.handleMessage);
  },
  beforeDestroy() {               // Vue 2 ─ keep beforeDestroy
    window.removeEventListener('message', this.handleMessage);
  },
  watch: {
    codeString(newCode) {
      this.postMessage({ type: 'update-code', code: newCode });
    }
  },
  methods: {
    /** ----------------------------------------------------------
     *  Concurrently load ALL files under
     *  public/users/<game.creator.id>/<game.id>/ using Promise.allSettled()
     * --------------------------------------------------------- */
    async preloadAssets() {
      try {
        const storage = getStorage(firebaseApp);
        const dirRef  = storageRef(
          storage,
          `public/users/${this.game.creator.id}/${this.game.id}/`
        );

        const { items } = await listAll(dirRef);
        if (!items.length) return;

        /* Create one promise per StorageReference   */
        const tasks = items.map(async (itemRef) => {
          try {
            // Kick off both network requests together
            const [meta, url] = await Promise.all([
              getMetadata(itemRef),
              getDownloadURL(itemRef)
            ]);

            const name = itemRef.name.toLowerCase();
            const type = meta.contentType || '';

            return { name, url, type };
          } catch (err) {
            console.warn('Skipped file:', itemRef.fullPath, err);
            return null;          // keep array indices aligned
          }
        });

        /* Wait for all to finish (never rejects) */
        const results = await Promise.allSettled(tasks);

        const sounds = {};
        const images = {};

        for (const r of results) {
          if (r.status !== 'fulfilled' || !r.value) continue;
          const { name, url, type } = r.value;

          if (type.startsWith('image/'))      images[name] = url;
          else if (type.startsWith('audio/')) sounds[name] = url;
        }

        this.soundsMap = sounds;
        this.imagesMap = images;

        /* Handy debug output */
        console.info(
          `Loaded ${Object.keys(images).length} images`,
          `and ${Object.keys(sounds).length} sounds`
        );
      } catch (err) {
        console.error('GameEngine: failed to preload assets', err);
      }
    },

    /** Ensure assets are ready, then init sketch */
    async onIframeLoad() {
      if (
        this.game.creator.id &&
        this.game.id &&
        !Object.keys(this.soundsMap).length &&
        !Object.keys(this.imagesMap).length
      ) {
        await this.preloadAssets();
      }

      this.postMessage({
        type:      'init',
        code:      this.codeString,
        soundsMap: this.soundsMap,
        imagesMap: this.imagesMap
      });
    },

    postMessage(msg) {
      const iframe = this.$refs.iframe;
      iframe?.contentWindow?.postMessage(msg, '*');
    },

    handleMessage(event) {
      if (event?.data?.type === 'p5-error') {
        this.$emit('error', event.data);
      }
    }
  }
};
</script>

<style scoped>
.aspect-ratio-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 75%;
  background: #eee;
}
.game-engine {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
